import React, { useState } from 'react';
import { Collapse } from 'antd';
import { FileWordOutlined } from '@ant-design/icons';
import type { Attachment } from '../../types';
import type { DockingRecord } from '../../types/docking';
import {
  attachmentLabelFromStorageName,
  inferAttachmentType,
} from '../../utils/parkAttachments';
import { formatMaturityStars } from '../../utils/dockingHelpers';
import { AttachmentPreviewModal } from './AttachmentPreviewModal';

const { Panel } = Collapse;

interface DockingRecordCardProps {
  record: DockingRecord;
}

function DetailRow(props: { label: string; children: React.ReactNode }) {
  return (
    <div className="dash-detail-row">
      <div className="dash-detail-row__label">{props.label}</div>
      <div className="dash-detail-row__value">{props.children}</div>
    </div>
  );
}

function joinList(items: string[]): string {
  return items.filter(Boolean).join('、') || '—';
}

export function DockingRecordCard(
  props: DockingRecordCardProps,
): React.ReactElement {
  const { record } = props;
  const [preview, setPreview] = useState<Attachment | null>(null);

  const memoAttachment: Attachment | null = (() => {
    if (!record.memoPath || !record.memoFileName) return null;
    const type = inferAttachmentType(record.memoFileName);
    if (!type) return null;
    return {
      label: record.memoFileName,
      path: record.memoPath,
      type,
    };
  })();

  const header = (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%' }}>
      <span style={{ color: 'rgba(255,255,255,0.85)', fontWeight: 500 }}>
        {record.dockedAt}
      </span>
      <span style={{ color: 'rgba(255,255,255,0.55)', fontSize: 12, flex: 1 }}>
        {joinList(record.ourParticipants)}
      </span>
    </div>
  );

  return (
    <>
      <Collapse ghost className="docking-record-collapse" defaultActiveKey={[record.id]}>
        <Panel key={record.id} header={header}>
          <div className="docking-card-body">
            <DetailRow label="对接日期">{record.dockedAt || '—'}</DetailRow>
            <DetailRow label="填报时间">{record.filedAt || '—'}</DetailRow>
            <DetailRow label="项目成熟度">
              {formatMaturityStars(record.maturity)}
            </DetailRow>
            <DetailRow label="对接人员">
              {joinList(record.ourParticipants)}
            </DetailRow>
            <DetailRow label="对接单位人员">
              {joinList(record.counterpartyParticipants)}
            </DetailRow>
            <DetailRow label="对接情况">
              {record.summary ? (
                <span className="dash-detail-text-block">{record.summary}</span>
              ) : (
                '—'
              )}
            </DetailRow>
            <DetailRow label="当前存在问题">
              {record.issues ? (
                <span className="dash-detail-text-block">{record.issues}</span>
              ) : (
                '—'
              )}
            </DetailRow>
            <DetailRow label="后续工作安排">
              {record.followUpPlan ? (
                <span className="dash-detail-text-block">{record.followUpPlan}</span>
              ) : (
                '—'
              )}
            </DetailRow>
            <DetailRow label="计划完成时间">
              {record.plannedCompletedAt || '—'}
            </DetailRow>
            <DetailRow label="备忘录">
              {record.memoFileName ? (
                memoAttachment ? (
                  <button
                    type="button"
                    className="docking-memo-card"
                    onClick={() => setPreview(memoAttachment)}
                  >
                    <FileWordOutlined className="docking-memo-card__icon" />
                    <span className="docking-memo-card__name">
                      {attachmentLabelFromStorageName(record.memoFileName)}
                    </span>
                    <span className="docking-memo-card__action">预览</span>
                  </button>
                ) : (
                  record.memoFileName
                )
              ) : (
                '—'
              )}
            </DetailRow>
          </div>
        </Panel>
      </Collapse>
      <AttachmentPreviewModal
        open={preview !== null}
        attachment={preview}
        onClose={() => setPreview(null)}
      />
    </>
  );
}
